import { CardRowProps } from '@uma/react-commons/dist/components/Card/CardWrapper/CardRow';
import {
  Card,
  getStorePageLinks,
  Loading,
  ROW_TYPE,
  StorePageLinksProps
} from '@uma/react-commons/dist/index';
import { IconPlusOpenAll, IconShoppingCart } from '@veneer/core';
import React, { useEffect, useState } from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { FeatureFlagProps } from 'src/pages/Orders';
import { URLS } from 'src/utils/urls';
import {
  HpStoreOrdersButtonClicked,
  ManageMySubscriptionsButtonClicked
} from './analytics';

const MoreOptionsOrder = ({
  isLoading,
  ordersCountryBasedExperience
}: FeatureFlagProps): React.JSX.Element | null => {
  const { translate, northboundAPIs, logger } = useDependencyManagerContext();
  const { country, language } = northboundAPIs.v1.localization || {};

  const [isFetching, setIsFetching] = useState(true);
  const [urlMyOrders, setUrlMyOrders] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    if (!isLoading && !ordersCountryBasedExperience) {
      setIsFetching(false);
      return;
    }

    async function fetchMainStoreLink() {
      try {
        const response = await getStorePageLinks(
          language,
          StorePageLinksProps.MainStoreLink
        );
        setUrlMyOrders(response);
      } catch (error) {
        setUrlMyOrders(URLS.MyOrders);
        logger?.error('Error fetching main store link:', error);
      } finally {
        setIsFetching(false);
      }
    }
    fetchMainStoreLink();
  }, [isLoading, language, logger, ordersCountryBasedExperience]);

  const shouldSeeMyOrdersLink = ordersCountryBasedExperience
    ? !!urlMyOrders
    : true;

  const rows: CardRowProps[] = [];

  rows.push({
    index: 0,
    icon: <IconPlusOpenAll />,
    type: ROW_TYPE.INTERNAL,
    content: translate(
      'orders.viewOrManageSubscriptions',
      'View or manage your subscriptions'
    ) as string,
    link: {
      pathname: '/subscriptions',
      dataTestId: 'subscriptions-link'
    },
    analyticsEvent: ManageMySubscriptionsButtonClicked(country, language)
  });

  if (isFetching || isLoading) {
    rows.push({
      index: 1,
      content: <Loading testid="loading-my-orders-link" />
    });
  } else if (shouldSeeMyOrdersLink) {
    rows.push({
      index: 1,
      icon: <IconShoppingCart />,
      type: ROW_TYPE.EXTERNAL,
      content: translate(
        'orders.explore',
        'Explore the latest laptops, desktops, printers and accessories'
      ) as string,
      link: {
        url: urlMyOrders || URLS.MyOrders,
        dataTestId: 'hp-store-orders'
      },
      analyticsEvent: HpStoreOrdersButtonClicked
    });
  }

  if (!shouldSeeMyOrdersLink && !isLoading && !isFetching) {
    return null;
  }

  return (
    <Card
      title={{
        content: translate('orders.moreOptions', 'More options') as string
      }}
      rows={rows}
      data-testid="more-options-order"
    />
  );
};

export default MoreOptionsOrder;
